"use client";

import type React from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Video,
  Upload,
  Send,
  Palette,
  Sparkles,
  Wand2,
  Type,
  Settings,
  ChevronDown,
  RefreshCw,
  Heart,
  Download,
  Zap,
  History,
  X,
  Share,
  Camera,
  VideoIcon,
  Play,
  Info,
  Gift,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { GoogleGenAI, type GenerateVideosParameters } from "@google/genai";
import uploadImageToFirebase from "../../utils/uploadImageToFirebase";
import PublishContent from "./PublishContent";
import { useDispatch, useSelector } from "react-redux";
import {
  addLibraryItem,
  selectLibraryContent,
  selectUserGlobalData,
} from "../../store/dreamworldSlice";
import dreamWorldProvider from "../../DreamWorldProvider";
import { LibraryItem } from "../../types/library";
import { AppDispatch } from "../../store/store";
import eventBus from "../../utils/eventBus";
import UploadsPanel from "../uploads/UploadsPanel";

import { v4 as uuidv4 } from "uuid";
import { Timestamp } from "firebase/firestore";
import { effects } from "../../data/effects";
import { Effect } from "../../types/effects";
import { ref, getBlob } from "firebase/storage";
import { storage } from "../../utils/firebase";
import EffectsPanel from "./EffectsPanel";
import LoadingIndicator from "../ui/LoadingIndicator";
import { hasEnoughFlux, decreaseFluxBalance } from "../../utils/fluxManager";
import InsufficientFluxModal from "../modals/InsufficientFluxModal";
import TipCreatorModal from "../modals/TipCreatorModal";
import { UserData } from "../../store/dreamworldSlice";

interface VideoGeneratorProps {
  effect?: Effect;
  isTesting?: boolean;
}
function blobToBase64(blob: Blob) {
  return new Promise<string>(async (resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      resolve(url.split(",")[1]);
    };
    reader.readAsDataURL(blob);
  });
}

const VideoGenerator: React.FC<VideoGeneratorProps> = ({
  effect: initialEffect,
  isTesting: initialIsTesting = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(selectUserGlobalData);
  const libraryContent = useSelector(selectLibraryContent);
  // Existing State
  const [prompt, setPrompt] = useState(initialEffect?.prompt || "");
  const [isGenerating, setIsGenerating] = useState(false);
  const [base64data, setBase64data] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // New State matching Image Generator
  const [selectedStyle, setSelectedStyle] = useState("Cinematic");
  const [stylesOpen, setStylesOpen] = useState(true);
  const [quickIdeasOpen, setQuickIdeasOpen] = useState(true);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [seed, setSeed] = useState("");

  // Results and history state
  const [generatedVideos, setGeneratedVideos] = useState<LibraryItem[]>([]);
  const [history, setHistory] = useState<LibraryItem[]>([]);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isGeneratingWithEffect, setIsGeneratingWithEffect] = useState(false);

  // Publish modal state
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedVideoForPublish, setSelectedVideoForPublish] =
    useState<LibraryItem | null>(null);
  const [isUploadsPanelOpen, setIsUploadsPanelOpen] = useState(false);
  const [isEffectsPanelOpen, setIsEffectsPanelOpen] = useState(false);
  const [currentEffect, setCurrentEffect] = useState<Effect | undefined>(
    initialEffect
  );

  const resultsRef = useRef<HTMLDivElement>(null);

  const [hasSufficientFlux, setHasSufficientFlux] = useState(true);
  const [isFluxModalOpen, setIsFluxModalOpen] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [creator, setCreator] = useState<UserData | null>(null);

  useEffect(() => {
    if (userGlobalData) {
      const fluxCost = currentEffect?.fluxCost || 25;
      hasEnoughFlux(userGlobalData.user.uid, fluxCost).then(
        setHasSufficientFlux
      );
    }
    if (currentEffect && currentEffect.creatorId !== "dreamworld") {
      dreamWorldProvider
        .getuserData(currentEffect.creatorId)
        .then(setCreator);
    }
  }, [userGlobalData, currentEffect]);

  // Load and save history
  useEffect(() => {
    const savedHistory = localStorage.getItem("videoGeneratorHistory");
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(parsedHistory);
      } catch (error) {
        console.error("Error loading history:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (history.length > 0) {
      const limitedHistory = history.slice(0, 100);
      localStorage.setItem(
        "videoGeneratorHistory",
        JSON.stringify(limitedHistory)
      );
    }
  }, [history]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [prompt]);

  // Scroll to results on mobile
  useEffect(() => {
    if (generatedVideos.length > 0 && window.innerWidth <= 768) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 500);
    }
  }, [generatedVideos]);
  useEffect(() => {
    if (base64data && currentEffect?.prompt) {
      eventBus.emit("fab:config", {
        icon: "generate",
        text: "Generate",
        action: handleGenerate,
      });
    } else {
      eventBus.emit("fab:reset");
    }
  }, [base64data, userGlobalData, currentEffect, hasSufficientFlux]);
  useEffect(() => {
    if (prompt.trim() && !currentEffect?.prompt) {
      eventBus.emit("fab:config", {
        icon: "generate",
        text: "Generate",
        action: handleGenerate,
      });
    } else {
      eventBus.emit("fab:reset");
    }
  }, [prompt, userGlobalData, currentEffect, hasSufficientFlux]);
  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const b64 = await blobToBase64(file);
      setBase64data(b64);
    }
  };

  const [isTesting, setIsTesting] = useState(initialIsTesting);
  const handleGenerate = async () => {
    if (!prompt || !userGlobalData) return;

    if (currentEffect && !isTesting) {
      await dreamWorldProvider.processEffectPayment(
        userGlobalData.user.uid,
        currentEffect
      );
    } else {
      const fluxCost = currentEffect?.fluxCost || 25;
      if (!hasSufficientFlux) {
        setIsFluxModalOpen(true);
        return;
      }
      const success = await decreaseFluxBalance(
        userGlobalData.user.uid,
        fluxCost
      );
      if (!success) {
        setIsGenerating(false);
        setHasSufficientFlux(false);
        setIsFluxModalOpen(true);
        if (currentEffect) {
          setIsGeneratingWithEffect(false);
        }
        return;
      }
    }

    if (currentEffect) {
      setIsGeneratingWithEffect(true);
    }
    setIsGenerating(true);
    setGeneratedVideos([]);

    try {
      const ai = new GoogleGenAI({
        apiKey: "AIzaSyDKqbfR_vItoj4GvwzBv8lBfyJajcEuqaA",
      });

      const config: GenerateVideosParameters = {
        model: "veo-2.0-generate-001",
        prompt: currentEffect?.prompt
          ? currentEffect?.prompt
          : `${selectedStyle} style: ${prompt}`,
        config: {
          numberOfVideos: 1,
        },
      };

      if (base64data) {
        config.image = {
          imageBytes: base64data,
          mimeType: "image/png",
        };
      }

      let operation = await ai.models.generateVideos(config);

      while (!operation.done) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        operation = await ai.operations.getVideosOperation({ operation });
      }

      const videos = operation.response?.generatedVideos;
      if (videos && videos.length > 0) {
        const newVideos: LibraryItem[] = [];

        for (const generatedVideo of videos) {
          if (generatedVideo.video?.uri) {
            const url = decodeURIComponent(generatedVideo.video.uri);
            const res = await fetch(
              `${url}&key=AIzaSyDKqbfR_vItoj4GvwzBv8lBfyJajcEuqaA`
            );
            const blob = await res.blob();
            const file = new File([blob], "generated_video.mp4", {
              type: "video/mp4",
            });
            const downloadUrl = await uploadImageToFirebase(
              file,
              "postsImages"
            );
            const docId = uuidv4();
            const newLibraryItem: LibraryItem = {
              id: docId,
              ownerId: userGlobalData.user.uid,
              type: "Video",
              fullUrl: downloadUrl,
              thumbnailUrl: downloadUrl, // Placeholder, should be generated
              prompt: prompt,
              isFavorite: false,
              views: 0,
              likes: 0,
              tags: [selectedStyle.toLowerCase()],
              size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
              createdAt: Timestamp.now(),
              isPublished: false,
              title: prompt,
              effectId: currentEffect?.id,
            };

            await dreamWorldProvider.saveLibraryItem(newLibraryItem);
            dispatch(addLibraryItem(newLibraryItem));

            newVideos.push(newLibraryItem);
          }
        }

        setGeneratedVideos(newVideos);

        setHistory((prev) => [...newVideos, ...prev]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsGenerating(false);
      if (currentEffect) {
        setIsGeneratingWithEffect(false);
      }
    }
  };

  const loadHistoryItem = (historyItem: LibraryItem) => {
    setPrompt(historyItem.prompt as string);
    setSelectedStyle(historyItem.tags[0]);
    setGeneratedVideos([historyItem]);
    setCurrentHistoryId(historyItem.id);
    setIsHistoryOpen(false);
  };

  const handlePublish = (video: LibraryItem) => {
    setSelectedVideoForPublish(video);
    setPublishModalOpen(true);
  };

  const handleImageSelect = async (image: LibraryItem) => {
    setIsUploadsPanelOpen(false);
    // Extract object path from fullUrl
    const match = decodeURIComponent(image.fullUrl).match(/o\/(.*?)\?/);
    if (!match) throw new Error("Invalid Firebase URL");
    const objectPath = match[1]; // e.g. "posts/3096be64-34ee-408f-a16e-3e32c9fbc40b"
    const imageRef = ref(storage, objectPath);
    const blob = await getBlob(imageRef);
    const b64 = await blobToBase64(blob);
    setBase64data(b64);
  };

  const styles = [
    {
      name: "Cinematic",
      icon: VideoIcon,
      gradient: "from-orange-500 to-red-600",
    },
    {
      name: "Documentary",
      icon: Camera,
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      name: "Animation",
      icon: Sparkles,
      gradient: "from-blue-500 to-cyan-600",
    },
    {
      name: "Abstract",
      icon: Wand2,
      gradient: "from-violet-500 to-purple-600",
    },
    { name: "Realistic", icon: Type, gradient: "from-gray-500 to-slate-600" },
    {
      name: "Artistic",
      icon: Palette,
      gradient: "from-purple-500 to-pink-600",
    },
  ];

  const examplePrompts = [
    "A serene ocean wave crashing on shore",
    "Time-lapse of city lights at night",
    "A butterfly landing on a flower",
    "Abstract flowing liquid colors",
  ];

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-73px)]">
      {/* Left Panel - Controls */}
      <div className="lg:w-96 flex-shrink-0 p-4 border-b lg:border-b-0 lg:border-r border-white/10 lg:overflow-y-auto">
        <div className="mb-4">
          {!currentEffect && (
            <motion.button
              onClick={() => setIsEffectsPanelOpen(true)}
              className="w-full flex items-center justify-center gap-2 p-3 bg-purple-600/80 backdrop-blur-sm border border-purple-500/50 rounded-lg hover:bg-purple-600/100 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Wand2 size={16} className="text-white" />
              <span className="text-sm font-medium text-white/90">
                Choose an Effect
              </span>
            </motion.button>
          )}
          <motion.button
            onClick={() => setIsHistoryOpen(true)}
            disabled={history.length === 0}
            className="w-full flex items-center justify-center gap-2 p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: history.length > 0 ? 1.02 : 1 }}
            whileTap={{ scale: history.length > 0 ? 0.98 : 1 }}
          >
            <History size={16} className="text-purple-400" />
            <span className="text-sm font-medium text-white/90">
              History ({history.length})
            </span>
          </motion.button>
        </div>
        {currentEffect && (
          <div className="p-4 rounded-lg bg-white/5 border border-white/10 mb-6">
            <h3 className="font-bold text-lg text-white mb-2">
              {currentEffect.title}
            </h3>
            <p className="text-sm text-white/70 mb-4">
              {currentEffect.description}
            </p>
            <div className="flex items-start gap-3 bg-white/5 p-3 rounded-md">
              <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-300">
                {currentEffect.recommendedImageDesc}
              </p>
            </div>
          </div>
        )}
        {/* File Upload */}
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-3 text-white/90 flex items-center gap-2">
            <Upload size={16} />
            Upload Image{" "}
            {currentEffect?.prompt
              ? "(Required to apply effect)"
              : "(Optional)"}
          </h3>
          <div
            onClick={() => setIsUploadsPanelOpen(true)}
            className="w-full text-center p-6 bg-white/5 backdrop-blur-sm border-2 border-dashed border-white/20 rounded-lg hover:bg-white/10 transition-all duration-300 cursor-pointer"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept="image/*"
            />
            <div className="flex flex-col items-center justify-center">
              <Upload size={24} className="text-white/60 mb-2" />
              <p className="text-sm text-white/70">
                {base64data ? "Image selected" : "Click to select an image"}
              </p>
            </div>
          </div>
        </div>
        {/* Prompt Input, Style, and Quick Ideas */}
        {!currentEffect?.prompt && (
          <>
            <div className="mb-6">
              <div
                className={`relative bg-white/5 backdrop-blur-xl border-2 ${
                  isFocused ? "border-purple-500/50" : "border-white/10"
                } rounded-xl transition-all duration-300 overflow-hidden`}
              >
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={handlePromptChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder="Describe the video you want to create..."
                  className="w-full bg-transparent p-4 text-sm placeholder-white/40 text-white focus:outline-none resize-none overflow-hidden leading-relaxed"
                  rows={3}
                />
                <motion.button
                  onClick={handleGenerate}
                  disabled={!prompt || isGenerating || !hasSufficientFlux}
                  className="absolute bottom-2 right-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                  whileHover={{
                    scale:
                      prompt && !isGenerating && hasSufficientFlux ? 1.05 : 1,
                  }}
                  whileTap={{
                    scale:
                      prompt && !isGenerating && hasSufficientFlux ? 0.95 : 1,
                  }}
                >
                  {isGenerating ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={16} />
                      <div className="flex items-center gap-1 text-xs">
                        <Zap size={12} />
                        <span>25</span>
                      </div>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
            <div className="mb-6">
              <motion.button
                onClick={() => setStylesOpen(!stylesOpen)}
                className="flex items-center justify-between w-full"
              >
                <h3 className="text-sm font-medium text-white/90 flex items-center gap-2">
                  <Palette size={16} />
                  Style
                </h3>
                <motion.div
                  animate={{ rotate: stylesOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown size={16} className="text-white/60" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {stylesOpen && (
                  <motion.div
                    initial={false}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div
                      className="mt-3 flex overflow-x-auto gap-2 pb-2"
                      style={{ scrollbarWidth: "none" }}
                    >
                      <div className="grid grid-rows-2 grid-flow-col gap-2">
                        {styles.map((style) => {
                          const Icon = style.icon;
                          return (
                            <motion.button
                              key={style.name}
                              onClick={() => setSelectedStyle(style.name)}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={`relative p-3 rounded-lg transition-all duration-300 ${
                                selectedStyle === style.name
                                  ? "bg-white/10 border-2 border-purple-500/50"
                                  : "bg-white/5 border-2 border-white/10 hover:bg-white/10"
                              }`}
                            >
                              <div
                                className={`w-6 h-6 rounded-md bg-gradient-to-r ${style.gradient} flex items-center justify-center mb-1 mx-auto`}
                              >
                                <Icon size={12} className="text-white" />
                              </div>
                              <p className="text-xs font-medium text-white/80">
                                {style.name}
                              </p>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="mb-6">
              <motion.button
                onClick={() => setQuickIdeasOpen(!quickIdeasOpen)}
                className="flex items-center justify-between w-full"
              >
                <h3 className="text-sm font-medium text-white/90 flex items-center gap-2">
                  <Zap size={16} />
                  Quick Ideas
                </h3>
                <motion.div
                  animate={{ rotate: quickIdeasOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown size={16} className="text-white/60" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {quickIdeasOpen && (
                  <motion.div
                    initial={false}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div
                      className="mt-3 flex overflow-x-auto gap-2 pb-2"
                      style={{ scrollbarWidth: "none" }}
                    >
                      <div className="grid grid-rows-2 grid-flow-col gap-2">
                        {examplePrompts.map((examplePrompt, index) => (
                          <motion.button
                            key={index}
                            onClick={() => setPrompt(examplePrompt)}
                            whileHover={{ scale: 1.01 }}
                            className="w-40 text-left p-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300 group"
                          >
                            <p className="text-xs text-white/70 group-hover:text-white transition-colors">
                              {examplePrompt}
                            </p>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}

        {currentEffect && (
          <div className="mt-6 mb-2">
            <motion.button
              onClick={handleGenerate}
              disabled={!base64data || isGenerating || !hasSufficientFlux}
              className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{
                scale:
                  base64data && !isGenerating && hasSufficientFlux ? 1.02 : 1,
              }}
              whileTap={{
                scale:
                  base64data && !isGenerating && hasSufficientFlux ? 0.98 : 1,
              }}
            >
              {isGeneratingWithEffect ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  <span>Generate with Effect</span>
                  {currentEffect.fluxCost && (
                    <div className="flex items-center gap-1 text-sm bg-black/20 px-2 py-1 rounded-full">
                      <Zap size={14} />
                      <span>{currentEffect.fluxCost}</span>
                    </div>
                  )}
                </>
              )}
            </motion.button>
          </div>
        )}

        <div>
          <motion.button
            onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
            className="flex items-center justify-between w-full p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex items-center gap-2">
              <Settings size={16} className="text-purple-400" />
              <span className="text-sm font-medium text-white/90">
                Advanced
              </span>
            </div>
            <motion.div
              animate={{ rotate: advancedSettingsOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={16} className="text-white/60" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {advancedSettingsOpen && (
              <motion.div
                initial={false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 mt-2 space-y-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg">
                  <div>
                    <h4 className="text-xs font-medium text-white/80 mb-2">
                      Seed
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={seed}
                        onChange={(e) => setSeed(e.target.value)}
                        placeholder="Random"
                        className="flex-1 bg-white/10 border border-white/20 p-2 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500/50 text-white placeholder-white/40 transition-all"
                      />
                      <motion.button
                        onClick={() =>
                          setSeed(String(Math.floor(Math.random() * 100000)))
                        }
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-2 bg-white/10 border border-white/20 rounded hover:bg-white/20 transition-all"
                      >
                        <RefreshCw size={12} className="text-white/80" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Panel - Results */}
      <div className="flex-1 p-4 lg:overflow-y-auto" ref={resultsRef}>
        <AnimatePresence mode="wait">
          {isGenerating ? (
            <LoadingIndicator key="loading" size="w-16 h-16" />
          ) : generatedVideos.length > 0 ? (
            <ResultsGrid
              key="results"
              videos={generatedVideos}
              onPublish={handlePublish}
              onTip={() => setShowTipModal(true)}
              showTipButton={
                !isTesting &&
                !!currentEffect &&
                currentEffect.creatorId !== "dreamworld"
              }
            />
          ) : (
            <EmptyState key="empty" effect={currentEffect} />
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isHistoryOpen && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end"
            onClick={() => setIsHistoryOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full max-w-md bg-gray-900/95 backdrop-blur-xl border-l border-white/10 p-6 overflow-y-auto pt-16 md:pt-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <History size={20} className="text-purple-400" />
                  Generation History
                </h2>
                <button
                  onClick={() => setIsHistoryOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} className="text-white/60" />
                </button>
              </div>

              <div className="space-y-3">
                {history.map((item) => (
                  <motion.div
                    key={item.id}
                    onClick={() => loadHistoryItem(item)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all duration-300 hover:bg-white/10 ${
                      currentHistoryId === item.id
                        ? "bg-purple-500/20 border-purple-500/50"
                        : "bg-white/5 border-white/10"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 flex-shrink-0 bg-white/10 rounded flex items-center justify-center">
                        <Play size={20} className="text-white/60" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/90 line-clamp-2 mb-1">
                          {item.prompt}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-white/60">
                          <span>{item.tags[0]}</span>
                          <span>•</span>
                          <span>{item.type}</span>
                        </div>
                        <p className="text-xs text-white/40 mt-1">
                          {new Date(
                            (item.createdAt as Timestamp).seconds * 1000
                          ).toLocaleDateString()}
                        </p>
                        {currentHistoryId === item.id && (
                          <div className="flex items-center gap-1 mt-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="text-xs text-purple-400">
                              Active
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {history.length === 0 && (
                <div className="text-center py-12">
                  <History size={48} className="text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">No generations yet</p>
                  <p className="text-white/40 text-sm mt-1">
                    Your generation history will appear here
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {publishModalOpen && selectedVideoForPublish && (
        <PublishContent
          item={selectedVideoForPublish}
          effectId={currentEffect?.id}
          onClose={() => {
            setPublishModalOpen(false);
            setSelectedVideoForPublish(null);
          }}
        />
      )}
      <UploadsPanel
        isOpen={isUploadsPanelOpen}
        onClose={() => setIsUploadsPanelOpen(false)}
        onImageSelect={handleImageSelect}
      />
      <EffectsPanel
        isOpen={isEffectsPanelOpen}
        onClose={() => setIsEffectsPanelOpen(false)}
        onSelectEffect={(effect) => {
          setCurrentEffect(effect);
          setPrompt(effect.prompt);
          setIsEffectsPanelOpen(false);
        }}
      />
      <InsufficientFluxModal
        isOpen={isFluxModalOpen}
        onClose={() => setIsFluxModalOpen(false)}
      />
      {showTipModal && creator && (
        <TipCreatorModal
          creator={creator}
          onClose={() => setShowTipModal(false)}
          onSendTip={(amount) => {
            if (userGlobalData) {
              dreamWorldProvider.sendTip(
                userGlobalData.user.uid,
                creator.uid,
                amount
              );
            }
          }}
        />
      )}
    </div>
  );
};

// Empty State Component
const EmptyState: React.FC<{ effect?: Effect }> = ({ effect }) => {
  if (!effect) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center h-full"
      >
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
              <Video size={32} className="text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Video Generation
          </h2>
          <p className="text-white/60 mb-6 leading-relaxed">
            Describe your scene, choose a style, and let AI create stunning
            videos for you.
          </p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-600 rounded mb-2 flex items-center justify-center">
                <VideoIcon size={12} className="text-white" />
              </div>
              <p className="text-white/70">Cinematic videos</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded mb-2 flex items-center justify-center">
                <Camera size={12} className="text-white" />
              </div>
              <p className="text-white/70">Documentary style</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-cyan-600 rounded mb-2 flex items-center justify-center">
                <Sparkles size={12} className="text-white" />
              </div>
              <p className="text-white/70">Animated scenes</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="w-6 h-6 bg-gradient-to-r from-violet-500 to-purple-600 rounded mb-2 flex items-center justify-center">
                <Wand2 size={12} className="text-white" />
              </div>
              <p className="text-white/70">Abstract visuals</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  } else {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center h-full"
      >
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 relative">
            <img
              src={effect.image}
              alt={effect.title}
              className="w-full h-full object-cover rounded-full"
            />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{effect.title}</h2>
          <p className="text-white/60 mb-6 leading-relaxed">
            {effect.description}
          </p>
          <p className="text-sm text-blue-400">{effect.recommendedImageDesc}</p>
        </div>
      </motion.div>
    );
  }
};

interface ResultsGridProps {
  videos: LibraryItem[];
  onPublish: (video: LibraryItem) => void;
  onTip: () => void;
  showTipButton: boolean;
}

const ResultsGrid: React.FC<ResultsGridProps> = ({
  videos,
  onPublish,
  onTip,
  showTipButton,
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white/90 flex items-center gap-2">
          <Video size={20} className="text-purple-400" />
          Your Videos
        </h2>
        <span className="text-sm text-white/60">{videos.length} videos</span>
      </div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 gap-4 pb-4"
      >
        {videos.map((video) => (
          <motion.div key={video.id} variants={itemVariants}>
            <CompactVideoCard
              video={video}
              onPublish={onPublish}
              onTip={onTip}
              showTipButton={showTipButton}
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

interface VideoCardProps {
  video: LibraryItem;
  onPublish: (video: LibraryItem) => void;
  onTip: () => void;
  showTipButton: boolean;
}

const CompactVideoCard: React.FC<VideoCardProps> = ({
  video,
  onPublish,
  onTip,
  showTipButton,
}) => {
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = () => setIsLiked(!isLiked);
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = video.fullUrl;
    link.download = `video-${video.id}.mp4`;
    link.click();
  };
  const handleRegenerate = () => console.log("Regenerate:", video.id);

  return (
    <motion.div
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden group"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <div className="relative">
        <video
          src={video.fullUrl}
          controls
          className="w-full aspect-video object-cover rounded-t-xl"
          preload="metadata"
        />
      </div>

      <div className="p-3">
        <div className="flex items-center justify-center gap-2">
          <motion.button
            onClick={handleLike}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className={`flex-1 p-3 rounded-lg backdrop-blur-sm transition-all text-sm font-medium ${
              isLiked
                ? "bg-red-500/30 text-red-400 border border-red-500/40"
                : "bg-white/10 hover:bg-white/20 text-white/80 border border-white/20"
            }`}
          >
            <Heart
              size={16}
              fill={isLiked ? "currentColor" : "none"}
              className="mx-auto"
            />
          </motion.button>

          <motion.button
            onClick={handleDownload}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
          >
            <Download size={16} className="mx-auto" />
          </motion.button>

          <motion.button
            onClick={() => onPublish(video)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-purple-600/40 hover:bg-purple-600/60 text-purple-300 border border-purple-500/50 transition-all text-sm font-medium"
          >
            <Share size={16} className="mx-auto" />
          </motion.button>

          <motion.button
            onClick={handleRegenerate}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
          >
            <RefreshCw size={16} className="mx-auto" />
          </motion.button>
          {showTipButton && (
            <motion.button
              onClick={onTip}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-yellow-500/40 hover:bg-yellow-500/60 text-yellow-300 border border-yellow-500/50 transition-all text-sm font-medium"
            >
              <Gift size={16} className="mx-auto" />
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default VideoGenerator;
